import { ComponentPropsWithoutRef, CSSProperties, forwardRef, Ref } from 'react';
import styled from 'styled-components';
import { Icon, IconDictionary, IconProps } from '../icon';
import { theme } from './badge_text.theme';

type Variant = 'interactive' | 'brandDark' | 'brandLight' | 'neutralLight' | 'neutralDark' | 'success' | 'info' | 'warning' | 'danger';
type Size = 'medium' | 'small';

interface BadgeTextProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
	/**
	 * The visual representation of the badge.
	 * @default interactive
	 */
	variant?: Variant;

	/**
	 * The visual size of the badge.
	 * @default medium
	 */
	size?: Size;

	/**
	 * Use to add a icon in the badge.
	 */
	icon?: keyof IconDictionary;

	/**
	 * Use to override the icon color.
	 */
	iconColor?: CSSProperties['color'];

	/**
	 * Props passed to the icon.
	 */
	iconProps?: Omit<IconProps, 'type' | 'size'>;

	/**
	 * The content of the badge.
	 */
	children: string;
}

const elements = {
	container: styled.div`
		--font: inherit;
		--minHeight: auto;
		--background: transparent;
		--textColor: #000;

		display: inline-flex;
		align-items: center;
		justify-content: center;
		box-sizing: border-box;
		padding: ${theme.getValue('basePadding')};
		user-select: none;
		border-radius: ${theme.getValue('baseBorderRadius')};
		min-height: var(--minHeight);
		background: var(--background);
		color: var(--textColor);
		font: var(--font);
		flex-shrink: 0;

		// Variants
		&[data-variant='interactive'] {
			--background: ${theme.getValue('interactiveBackground')};
			--textColor: ${theme.getValue('interactiveTextColor')};
		}
		&[data-variant='brandDark'] {
			--background: ${theme.getValue('brandDarkBackground')};
			--textColor: ${theme.getValue('brandDarkTextColor')};
		}
		&[data-variant='brandLight'] {
			--background: ${theme.getValue('brandLightBackground')};
			--textColor: ${theme.getValue('brandLightTextColor')};
		}
		&[data-variant='neutralDark'] {
			--background: ${theme.getValue('neutralDarkBackground')};
			--textColor: ${theme.getValue('neutralDarkTextColor')};
		}
		&[data-variant='neutralLight'] {
			--background: ${theme.getValue('neutralLightBackground')};
			--textColor: ${theme.getValue('neutralLightTextColor')};
		}
		&[data-variant='success'] {
			--background: ${theme.getValue('successBackground')};
			--textColor: ${theme.getValue('successTextColor')};
		}
		&[data-variant='info'] {
			--background: ${theme.getValue('infoBackground')};
			--textColor: ${theme.getValue('infoTextColor')};
		}
		&[data-variant='warning'] {
			--background: ${theme.getValue('warningBackground')};
			--textColor: ${theme.getValue('warningTextColor')};
		}
		&[data-variant='danger'] {
			--background: ${theme.getValue('dangerBackground')};
			--textColor: ${theme.getValue('dangerTextColor')};
		}

		// Sizes
		&[data-size='small'] {
			--font: ${theme.getValue('smallText')};
			--minHeight: ${theme.getValue('smallHeight')};
		}
		&[data-size='medium'] {
			--font: ${theme.getValue('mediumText')};
			--minHeight: ${theme.getValue('mediumHeight')};
		}

		.adjustText {
			box-sizing: border-box;
			padding-left: ${theme.getValue('baseAdjustTextPadding')};
			position: relative;
		}
	`,
};

function BadgeText(props: BadgeTextProps, ref: Ref<HTMLDivElement>): JSX.Element {
	const { children, variant = 'interactive', size = 'medium', icon, iconColor, iconProps, ...rest } = props;

	const iconSize = size === 'medium' ? 'small' : 'xSmall';

	return (
		<elements.container data-variant={variant} data-size={size} {...rest} ref={ref}>
			{icon ? (
				<Icon
					{...iconProps}
					type={icon}
					size={iconSize}
					style={{ ...iconProps?.style, color: iconColor ?? iconProps?.style?.color }}
				/>
			) : null}

			<span className='adjustText'>{children}</span>
		</elements.container>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component for displaying a badge with data of the `string` type.
 *
 * @deprecated Use the `Chip` component instead.
 *
 * @example
 * ```tsx
 * import { BadgeText } from '@cellula/react';
 *
 * interface Props {
 *   text: string;
 * }
 *
 * function App({ text }: Props): JSX.Element {
 *   return (
 *     <BadgeText variant="BrandLight" size="small">
 *       {text}
 *     </BadgeText>
 *   );
 * }
 * ```
 */
const _BadgeText = forwardRef(BadgeText);

export type { BadgeTextProps };
export { _BadgeText as BadgeText };
